import React, { memo, useCallback, useEffect, useMemo, useState } from "react"
import QuickWidgetDialog from "../../../quickWidgetDialog/quickWidgetDialog"
import { useSelector } from "react-redux"
import { Box, Stack, Typography } from "@mui/material"
import { QuickWidgetTypes } from "../../../../reducer/user.reducer"
import { fetchQuickTrades } from "../../../../services/apis/quickTrades"
import { selectQuickTrades } from "../../../../selectors/quickTrades"
import { ExtraSmallButton, NonInteractiveTooltip } from "../../../custom"
import { QuickTradeProto } from "../../../../proto/generated/quick-trade"
import { strategyParser } from "../../../strategyTemplate/StrategyParser"
import { OptionChainViewKey } from "../../../../assets/constants"
import AddQuickTrade from "./AddQuickTrade"
import { selectPrimaryTradingAccount, selectTradingAccounts } from "../../../../selectors/user"
import { PlaceOrModifyOrdersRequest, TradingAccountStatus } from "../../../../proto/generated/trading-bridge"
import { openConnectAccountDialog, setOrderDetails, updateOrderHistory } from "../../../../reducer/orders.reducer"
import { checkForDuplicateOrders, isDatePassed } from "../../../../util/tradepanel"
import { Order, OrderStatus, OrderType, Product, TransactionType, transactionTypeFromJSON, Validity, Broker_Variety, Broker_OrderType } from "../../../../proto/generated/order"
import { useDispatch } from "react-redux"
import { v4 as uuid } from "uuid"
import { getExchangeAndCodeFromKey, getSecurityAsync, getSecurityKey } from "../../../../util/security"
import { getOrderPriceFromQuote, getOrderPricesFromQuote } from "../../../../util/quicktradewidget"
import AddIcon from "../../../../assets/icons/AddIcon"
import { setOpenQuickTradeSettingsDialog } from "../../../../reducer/quickTrades.reducer"
import { placeOrModifyOrder } from "../../../../services/apis/orders"
import commonActions from "../../../../actions/common.actions"
import { selectPrimarySecurity } from "../../../../selectors/home"
import { StrategyTemplateProto } from "../../../../proto/generated/strategy"
import QuickTradeTooltip from "./QuickTradeTooltip"
import Loading from "../../Loading"
import { Security, SecurityCategory_CategoryCode } from "../../../../proto/generated/security"
import ROUTES from "../../../../assets/routes"
import { matchPath, useLocation } from "react-router-dom"
import { addOrderAlertMessage } from "../../../../util/orders"

const autoSecurityDisabledRoutes = [ROUTES.reports, ROUTES.fiiDii, ROUTES.screener]

const QuickTradeWidget: React.FC = () => {
    const dispatch = useDispatch()
    const quickTrades = useSelector(selectQuickTrades)
    const [disableButtons, setDisableButtons] = useState(false)
    const tradingAccounts = useSelector(selectTradingAccounts)
    const location = useLocation()
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)
    const { key } = useSelector(selectPrimarySecurity)
    const [loading, setLoading] = useState(true)
    const { primary } = useSelector(selectPrimarySecurity)
    const [securityCache, setSecurityCache] = useState<Record<string, Security | null>>({})
    useEffect(() => {
        (async () => {
            if (quickTrades.length === 0) {
                setLoading(true)
                await fetchQuickTrades()
                setLoading(false)
            } else {
                setLoading(false)
            }
        })()
    }, [])

    const secKeysToLoad = useMemo(() => {
        const keys = new Set<string>()
        quickTrades.forEach((qt) => {
            const computed = qt?.security?.code ? qt?.security : primary
            if (computed?.exchange != null && computed?.code) {
                keys.add(getSecurityKey(computed.exchange, computed.code))
            }
        })
        if (primary?.exchange != null && primary?.code) {
            keys.add(getSecurityKey(primary.exchange, primary.code))
        }
        return Array.from(keys)
    }, [quickTrades, primary])

    useEffect(() => {
        if (!secKeysToLoad.length) return
        let cancelled = false
        Promise.all(secKeysToLoad.map((k) => getSecurityAsync(k))).then((secs) => {
            if (cancelled) return
            const next: Record<string, Security | null> = {}
            secKeysToLoad.forEach((k, i) => { next[k] = secs[i] ?? null })
            setSecurityCache((prev) => ({ ...prev, ...next }))
        })
        return () => { cancelled = true }
    }, [secKeysToLoad])

    const checkForButtonDisable = useCallback((quickTrade: QuickTradeProto) => {
        const autoSecurity = quickTrade?.security?.code ? false : true
        if (autoSecurity) {
            if (autoSecurityDisabledRoutes.some(route=>matchPath(route, location.pathname))) {
                return true
            }
        }
        const computedSecurity = quickTrade?.security?.code ? quickTrade?.security : primary
        const secKey = getSecurityKey(computedSecurity?.exchange??0, computedSecurity?.code??'')
        const security = secKey ? securityCache[secKey] : undefined
        if(security?.expiry && isDatePassed(security?.expiry?.expiry)) {
            return true
        }
        if(quickTrade?.templateOrderExecutionParameters?.transactionType) {
            if([SecurityCategory_CategoryCode.FUTURES, SecurityCategory_CategoryCode.OPTIONS, SecurityCategory_CategoryCode.STOCKS].includes(security?.categoryCode)) {
                return false
            }
            return true
        } else if(quickTrade?.templateOrderExecutionParameters?.strategyLegs?.strategyTemplateLegs.length>0) {
            if(security?.hasFo) {
                return false
            }
            return true
        }
        return true
    }, [primary, location.pathname, securityCache])

    const handleQuickTradeAction = async (quickTrade: QuickTradeProto) => {
        const underlyingSecKey = quickTrade.security.code ? getSecurityKey(quickTrade.security?.exchange ?? 0, quickTrade.security?.code ?? '') : key
        const mainSecurity = await getSecurityAsync(underlyingSecKey)
        const template: StrategyTemplateProto = {
            strategyTemplateLegs: quickTrade.templateOrderExecutionParameters.strategyLegs,
            name: ''
        } as StrategyTemplateProto
        if (!template || !underlyingSecKey) {
            console.error("Template not found for quick trade action")
            return
        }
        const [exchange, code] = getExchangeAndCodeFromKey(underlyingSecKey)
        let orderData: any = []
        if(!quickTrade.templateOrderExecutionParameters.transactionType) {
            orderData = await strategyParser(template, { code, exchange }, [], OptionChainViewKey.LIVE, true)
        }

        //Execute the quick trade action

        if ((primaryTradingAccount?.status !== TradingAccountStatus.ACTIVE)) {
            if (tradingAccounts.length === 0) {
                dispatch(openConnectAccountDialog({
                    type: 'connect',
                    tradingAccountId: primaryTradingAccount.accountId
                }))
            } else {
                dispatch(openConnectAccountDialog({
                    type: 'reconnect',
                    tradingAccountId: primaryTradingAccount.accountId
                }))
            }
        } else if (quickTrade?.templateOrderExecutionParameters?.strategyLegs?.strategyTemplateLegs?.length>0 && checkForDuplicateOrders(orderData.orders)) {
            return
        } else if (quickTrade?.templateOrderExecutionParameters?.transactionType) {
            const brokerOrderType = quickTrade?.templateOrderExecutionParameters?.brokerOrderType
            let quantity = 0
            if (quickTrade?.templateOrderExecutionParameters?.lots) {
                quantity = quickTrade?.templateOrderExecutionParameters?.lots * mainSecurity.lotSize
            } else if (quickTrade?.templateOrderExecutionParameters?.quantity) {
                quantity = quickTrade?.templateOrderExecutionParameters?.quantity
            }
            const orderPrice = (brokerOrderType === Broker_OrderType.MARKET_ORDER || brokerOrderType === Broker_OrderType.STOPLOSS_MARKET_ORDER) ? 0 : await getOrderPriceFromQuote(mainSecurity, quickTrade.templateOrderExecutionParameters.transactionType, quickTrade.templateOrderExecutionParameters.quoteType, quickTrade.templateOrderExecutionParameters.quoteValue)

            const batchId = uuid()
            const strategyGroup = `${mainSecurity.symbol} - ${quickTrade.actionName}`
            const request: PlaceOrModifyOrdersRequest = {
                tradingAccountId: primaryTradingAccount.accountId??'',
                buyBeforeSell: false,
                order: [{
                    security: {
                        exchange: mainSecurity.exchange,
                        code: mainSecurity.code
                    },
                    transactionType: quickTrade?.templateOrderExecutionParameters?.transactionType,
                    orderType: OrderType.NORMAL,
                    brokerOrderType,
                    quantity: Number(quantity),
                    product: Product.CNC,
                    price: brokerOrderType === Broker_OrderType.MARKET_ORDER || brokerOrderType === Broker_OrderType.STOPLOSS_MARKET_ORDER ? 0 : Math.round(Number(orderPrice) * (mainSecurity?.priceMultiplier ?? 100)),
                    triggerPrice: 0,
                    brokerVariety: Broker_Variety.REGULAR,
                    validity: Validity.DAY,
                    clientOrderId: uuid(),
                    orderDate: Date.now(),
                    isBasket: false,
                    batchId,
                    strategyGroup,
                    immediateExecutePaperOrder: false
                }]
            }
            try {
                const created = Date.now();
                const response = await placeOrModifyOrder(request)
                const order = response.orderResponse.length ? response.orderResponse[0] : null;
                if (!order) throw new Error();
                const {successful, commitOrder, brokerOrderId, orderId, errorMessage} = order;
                if (!successful) {
                    addOrderAlertMessage({
                        ...request.order[0],
                        status: OrderStatus.FAILED,
                        message: errorMessage || 'Unable to place order'
                    })
                } else {
                    const updateOrder: Order = {
                        ...request.order[0],
                        brokerOrderId,
                        status: OrderStatus.OPEN,
                        filledQuantity: 0,
                        orderId,
                        created
                    }
                    if (commitOrder)  {
                        dispatch(updateOrderHistory({ updates: [updateOrder] }))
                    }
                    addOrderAlertMessage({
                        ...updateOrder,
                        message: 'Order placed successfully'
                    })
                }
            } catch (error: any) {
                addOrderAlertMessage({
                    ...request.order[0],
                    status: OrderStatus.FAILED,
                    message: error.message || 'Unable to place order'
                })
            }
        } else {
            setDisableButtons(true)
            const updatedBasketId = uuid();
            const batchId = uuid()
            const basketMode = orderData.orders.length > 1
            const strategyGroup = `${mainSecurity.symbol} - ${quickTrade.actionName}`
            const orderPrices = await getOrderPricesFromQuote(orderData.orders, orderData.orderPrices, quickTrade.templateOrderExecutionParameters.quoteType, quickTrade.templateOrderExecutionParameters.quoteValue)
            let quantityMultiplier = quickTrade?.templateOrderExecutionParameters?.multiplier
            if (!quantityMultiplier && orderData.orders.length === 1 && orderData.orders[0].type === 'buy') {
                if (quickTrade?.templateOrderExecutionParameters.amount) {
                    const oneLotQunatity = (orderData.orders[0].quantity) * (orderData.orders[0].security?.lotSize ?? 0)
                    const price = Math.round(Number(orderPrices[0].price)) * oneLotQunatity
                    quantityMultiplier = Math.floor((quickTrade?.templateOrderExecutionParameters?.amount ?? 0) / price)
                    if (quantityMultiplier === 0) {
                        commonActions.setApiError(dispatch, {
                            error: true,
                            message: `Min. Amount required: ${price}, Your amount: ${quickTrade?.templateOrderExecutionParameters?.amount ?? 0}`,
                            errorType: 'error'
                        })
                        addOrderAlertMessage({
                            security: {
                                exchange: mainSecurity.exchange,
                                code: mainSecurity.code
                            },
                            status: OrderStatus.REJECTED,
                            message: `Min. Amount required: ${price}, Your amount: ${quickTrade?.templateOrderExecutionParameters?.amount ?? 0}`
                        } as Order)
                        setDisableButtons(false)
                        return
                    }
                } else {
                    quantityMultiplier = 1
                }
            }
            const orderDate = Date.now();
            const orderDetailsArray = await Promise.all(
                orderData.orders.map(async (order, i) => {
                    if (!order.security) {
                        console.error('Security is not defined for order', order)
                        throw new Error('Security is not defined for order');
                    }
                    const security = await getSecurityAsync(getSecurityKey(order.security.exchange ?? 0, order.security.code ?? ''));
                    if (!security) throw new Error('Security not present');
                    const newUuid = order.uuid
                    const orderDetails: Order = {
                        security: {
                            code: security?.code,
                            exchange: security?.exchange
                        },
                        transactionType: transactionTypeFromJSON(order.type.toUpperCase()),
                        orderType: OrderType.NORMAL,
                        brokerOrderType: quickTrade.templateOrderExecutionParameters?.brokerOrderType,
                        quantity: (order.quantity) * (order.security?.lotSize ?? 0) * (quantityMultiplier),
                        product: security.categoryCode === SecurityCategory_CategoryCode.STOCKS ? Product.CNC : Product.MARGIN,
                        price: quickTrade.templateOrderExecutionParameters?.brokerOrderType === Broker_OrderType.MARKET_ORDER ? 0 : Math.round(Number(orderPrices[i].price) * (mainSecurity?.priceMultiplier ?? 100)),
                        triggerPrice: 0,
                        brokerVariety: Broker_Variety.REGULAR,
                        validity: Validity.DAY,
                        clientOrderId: newUuid,
                        orderDate,
                        isBasket: basketMode,
                        strategyGroup,
                        immediateExecutePaperOrder: false
                    }

                    if (basketMode) {
                        orderDetails.basketId = updatedBasketId;
                        orderDetails.batchId = batchId;
                    }
                    dispatch(setOrderDetails({ index: i, orderDetails }))
                    return orderDetails;
                })
            )
            const request: PlaceOrModifyOrdersRequest = {
                tradingAccountId: primaryTradingAccount.accountId,
                buyBeforeSell: true,
                order: orderDetailsArray
            }
            try {
                const created = Date.now();
                const response = await placeOrModifyOrder(request)
                const order = response.orderResponse.length ? response.orderResponse[0] : null;
                if (!order) throw new Error();
                const { successful, commitOrder, brokerOrderId, orderId, errorMessage } = order;
                if (!successful) {
                    addOrderAlertMessage({
                        ...request.order[0],
                        status: OrderStatus.FAILED,
                        message: errorMessage || 'Unable to place order'
                    })
                } else {
                    const updateOrder: Order = {
                        ...request.order[0],
                        brokerOrderId,
                        status: OrderStatus.OPEN,
                        filledQuantity: 0,
                        orderId,
                        created
                    }
                    if (commitOrder) {
                        dispatch(updateOrderHistory({ updates: [updateOrder] }))
                    }
                    addOrderAlertMessage({
                        ...updateOrder,
                        message: 'Order placed successfully'
                    })
                }
            } catch (error: any) {
                addOrderAlertMessage({
                    ...request.order[0],
                    status: OrderStatus.FAILED,
                    message: error.message || 'Unable to place order'
                })
            }
            // }
            setDisableButtons(false)
        }
    }

    return (
        <QuickWidgetDialog widgetType={QuickWidgetTypes.QUICK_TRADE} title="Quick Trade">
            <Box sx={{ display: 'flex', gap: '8px', height: '100%', flexWrap: 'wrap', maxHeight: 'max-content', background: 'none', overflowY: 'auto', p: 1 }}>
                {
                    loading ?
                        <Loading size={'30px'} /> :
                        <>
                            {
                                quickTrades.length === 0 ? null :
                                    <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
                                        {
                                            quickTrades.map((q) => (
                                                <NonInteractiveTooltip
                                                    title={<QuickTradeTooltip quickTrade={q} />}
                                                    sx={{
                                                        '& .MuiTooltip-tooltip': {
                                                            background: theme => theme.palette.background.default,
                                                            width: 'min-content',
                                                            maxWidth: 'none'
                                                        },
                                                        '& .MuiTooltip-arrow': {
                                                            color: theme => theme.palette.background.default
                                                        }
                                                    }}
                                                    arrow
                                                >
                                                    <ExtraSmallButton
                                                        sx={{
                                                            maxWidth: '200px',
                                                            px: 1,
                                                            borderColor: theme => theme.palette[q.templateOrderExecutionParameters.transactionType ? (q.templateOrderExecutionParameters.transactionType === TransactionType.BUY ? 'buy' : 'sell') : 'primary'].main,
                                                        }}
                                                        variant="outlined"
                                                        color={q.templateOrderExecutionParameters.transactionType ? (q.templateOrderExecutionParameters.transactionType === TransactionType.BUY ? 'buy' : 'sell') : 'primary'}
                                                        key={q.id + '-action-btn'}
                                                        disabled={disableButtons || checkForButtonDisable(q)}
                                                        onClick={() => handleQuickTradeAction(q)}
                                                    >
                                                        <div style={{
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap"
                                                        }}>
                                                            {q.actionName}
                                                        </div>
                                                    </ExtraSmallButton>
                                                </NonInteractiveTooltip>
                                            ))
                                        }
                                    </Stack>
                            }
                            {
                                quickTrades.length === 0 ?
                                    <Box sx={{ height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <ExtraSmallButton variant="outlined" disabled={disableButtons} onClick={() => {
                                            dispatch(setOpenQuickTradeSettingsDialog({
                                                settingsDialogOpen: true,
                                                dialogMode: 'addOrEdit',
                                                editTemplateId: undefined
                                            }))
                                        }}>
                                            <AddIcon />
                                            {
                                                ' Add new action'
                                            }
                                        </ExtraSmallButton>
                                    </Box> :
                                    <NonInteractiveTooltip title={'Add new action'} arrow>
                                        <ExtraSmallButton variant="outlined" disabled={disableButtons} onClick={() => {
                                            dispatch(setOpenQuickTradeSettingsDialog({
                                                settingsDialogOpen: true,
                                                dialogMode: 'addOrEdit',
                                                editTemplateId: undefined
                                            }))
                                        }}>
                                            <AddIcon />
                                        </ExtraSmallButton>
                                    </NonInteractiveTooltip>
                            }
                        </>
                }
            </Box>
            <AddQuickTrade />
        </QuickWidgetDialog>
    )
}

export default memo(QuickTradeWidget)